import React from 'react';
import { Table, TableBody, TableCell, TableRow, Paper, Grid, Button } from '@mui/material';
import { UseFormSetValue } from 'react-hook-form';
import CommonActionButton from '@/components/common/CommonActionButton';
import HookFormControls from '@/components/common/HookFormControls';

// Types & Services
import { EmployeeDepartments } from '@/types/Employee.type';
import Status from '@/types/Status.type';
import Phone from '@/types/Phone.type';
import Style from '@/types/Style.type';

import ActSchedule from '@/types/ActSchedule.type';

type Props = {
  employeeDepartments: EmployeeDepartments;
  undecidedFlag: boolean;
  goingStraightHomeFlag: boolean;
  isSubmitting: boolean;
  validationRules: {
    style: {};
    startTime: {};
    endTime: {};
    nextDate: {};
    status: {};
    place: {};
    returnTime: {};
    phone: {};
    goingStraightHomeFlag: {};
    undecidedFlag: {};
  };
  phones: Array<Phone>;
  styles: Array<Style>;
  statuses: Array<Status>;
  handleClick1h: () => void;
  handleClick30m: () => void;
  setValue: UseFormSetValue<ActSchedule>;
  setUndecidedFlag: (undecidedFlag: boolean) => void;
  setGoingStraightHomeFlag: (goingStraightHomeFlag: boolean) => void;
  autoFocus: boolean;
};

const WidePlanRegistry: React.FC<Props> = ({
  employeeDepartments,
  undecidedFlag,
  goingStraightHomeFlag,
  isSubmitting,
  validationRules,
  phones,
  styles,
  statuses,
  handleClick1h,
  handleClick30m,
  setValue,
  setUndecidedFlag,
  setGoingStraightHomeFlag,
  autoFocus,
}) => {
  return (
    <Paper variant="outlined" sx={{width: "900px", margin: 'auto'}}>
      <Table>
        <TableBody>
          <TableRow>
            <TableCell align="left">氏名　{employeeDepartments.name}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell align="left">
              <HookFormControls
                label="勤務形態"
                type="autocomplete"
                name="style"
                getOptionLabel="name"
                rule={validationRules.style}
                options={styles}
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align="left">
              <Grid display="flex" gap={1}>
                <HookFormControls
                  name="startTime"
                  type="time"
                  label="勤務開始時間"
                  rule={validationRules.startTime}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  sx={{ maxWidth: 150 }}
                />
                <HookFormControls
                  name="endTime"
                  type="time"
                  label="勤務終了時間"
                  formHelperTextProps={{ sx: { whiteSpace: 'nowrap' } }}
                  rule={validationRules.endTime}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  sx={{ maxWidth: 150 }}
                />
              </Grid>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align="left">
              <Grid display="flex" gap={1}>
                <HookFormControls
                  disabled={undecidedFlag}
                  name="nextDate"
                  type="date"
                  label="次回出社日"
                  rule={validationRules.nextDate}
                  InputLabelProps={{ shrink: true }}
                  formHelperTextProps={{ sx: { whiteSpace: 'nowrap' } }}
                  sx={{ width: 220 }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ maxHeight: 56 }}
                  disabled={undecidedFlag}
                  onClick={() => setValue('nextDate', '')}
                >
                  Clear
                </Button>
                <HookFormControls
                  name="undecidedFlag"
                  type="checkbox"
                  label="未定"
                  formControlLabelProps={{ sx: { maxHeight: 56 } }}
                  rule={validationRules.undecidedFlag}
                  onClick={() => {
                    setValue('nextDate', '');
                    setUndecidedFlag(!undecidedFlag);
                  }}
                />
              </Grid>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align="left">
              <HookFormControls
                type="autocomplete"
                label="ステータス"
                getOptionLabel="name"
                name="status"
                rule={validationRules.status}
                options={statuses}
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align="left">
              <HookFormControls name="place" type="text" label="行先" rule={validationRules.place} multiline rows={3} />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align="left">
              <Grid display="flex" gap={1}>
                <HookFormControls
                  name="returnTime"
                  type="time"
                  label="戻り時間"
                  autoFocus={autoFocus}
                  disabled={goingStraightHomeFlag}
                  rule={validationRules.returnTime}
                  InputLabelProps={{ shrink: true }}
                  formHelperTextProps={{ sx: { whiteSpace: 'nowrap' } }}
                  sx={{ width: 150 }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  disabled={goingStraightHomeFlag}
                  sx={{ maxHeight: 56 }}
                  onClick={() => setValue('returnTime', '')}
                >
                  Clear
                </Button>
                <Button
                  disabled={goingStraightHomeFlag}
                  sx={{ maxHeight: 56 }}
                  variant="contained"
                  size="small"
                  onClick={handleClick30m}
                >
                  +30分
                </Button>
                <Button
                  disabled={goingStraightHomeFlag}
                  sx={{ maxHeight: 56 }}
                  variant="contained"
                  size="small"
                  onClick={handleClick1h}
                >
                  +1時間
                </Button>
                <HookFormControls
                  name="goingStraightHomeFlag"
                  type="checkbox"
                  label="直帰"
                  onClick={() => {
                    setValue('returnTime', '');
                    setGoingStraightHomeFlag(!goingStraightHomeFlag);
                  }}
                  formControlLabelProps={{ sx: { maxHeight: 56 } }}
                  rule={validationRules.goingStraightHomeFlag}
                />
              </Grid>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align="left">
              <HookFormControls
                type="autocomplete"
                name="phone"
                getOptionLabel="number"
                label="連絡先"
                rule={validationRules.phone}
                options={phones}
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <CommonActionButton isSubmitting={isSubmitting} />
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Paper>
  );
};
export default WidePlanRegistry;
